import React, {useCallback, useEffect, useState} from 'react';
import {ActivityIndicator, Linking, Platform, ScrollView, StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import {useFocusEffect} from '@react-navigation/native';
import type {AxiosError} from 'axios';
import {observer} from 'mobx-react';

import GamePlayActions from '../../components/activation/games/GamePlayActions';
import LoadingImg from '../../components/LoadingImg';
import PromotionUserSignInModal from '../../components/modals/PromotionUserSignInModal';
import OpenURLButton from '../../components/OpenURLButton';
import PromotionCaptureDetails, {
  PromotionUserSubmission,
} from '../../components/PromotionCaptureDetails';
import PromotionProductListing from '../../components/PromotionProductListing';
import {ERROR_MESSAGE} from '../../components/ToastBottom';
import Button from '../../components/ui-controls/Button';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Markdown from '../../components/ui-controls/Markdown';
import Text from '../../components/ui-controls/Text';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AirtablePromotionItem} from '../../models/airtable';
import {AppRoute} from '../../models/AppRoute';
import {CreateCompetitionEntryResponse} from '../../models/middleware';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
// Specific modules, not the `queries/promotions` barrel: neither of these needs
// the query hook, and the barrel would pull `queryClient` (whose module load
// starts TanStack persistence) into this screen's graph for no benefit.
import {loadLinkedProducts} from '../../queries/promotions/loadLinkedProducts';
import {
  isDrawnWinnersPromotion,
  isEcommerceOnlyPromotion,
  isInStoreOnlyPromotion,
} from '../../queries/promotions/predicates';
import {canViewMembersOnlyPromotion} from '../../stores/promotionAccess';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import apiService from '../../transport/apiService';
import {OrderLine} from '../../transport/apiServiceResponse';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName} from '../../utils/analytics/events';
import {getTermsAndConditionsUrl} from '../../utils/competitions';
import {getFeedDiscoverDetailGraphics} from '../../utils/feedAndDiscover/media';
import {formatDate} from '../../utils/functions';
import {isGiftCardsPromotion} from '../../utils/giftCards';
import {getHeroImageDetail} from '../../utils/heroImage';
import {logger, serializeError} from '../../utils/logger';
import {getEnterHereButtonText} from '../../utils/promotions';

import GiftCardPromotionExtraContent from './GiftCardPromotionExtraContent';
import MemberOnlyAccessView from './MemberOnlyAccessView';

type SuccessViewProps = {
  activation: AirtablePromotionItem;
  navigation: PromotionDetailScreenProps['navigation'];
  createCompetitionEntryResponse?: CreateCompetitionEntryResponse;
  onNotEnterGame?: () => void;
};
const SuccessView = ({
  activation: promotion,
  navigation,
  createCompetitionEntryResponse,
  onNotEnterGame = () => {},
}: SuccessViewProps) => {
  const styles = StyleSheet.create({
    container: {
      justifyContent: 'space-around',
      alignItems: 'center',
      height: 150,
      marginTop: 10,
      paddingVertical: 10,
      backgroundColor: '#c5edc5',
    },
  });

  const {adhocPromotionMessage, endDate} = promotion;
  const summerCashGrabConfig = createCompetitionEntryResponse?.meta?.game?.SUMMER_CASH_GRAB;

  const copy = {
    default: {
      title: 'GOOD LUCK!',
      body: ['YOU ARE IN THE DRAW', 'WINNERS WILL BE ANNOUNCED', 'BY EMAIL'],
    },
    game: {
      title: 'GOOD LUCK!',
      body: ['THANK YOU FOR PLAYING'],
    },
  };

  const {title, body} = promotion.game ? copy.game : copy.default;

  return (
    <>
      <View style={styles.container}>
        {adhocPromotionMessage ? (
          // On web (react-native-web) styles are ignored #44
          // https://github.com/iamacup/react-native-markdown-display/issues/44
          // This was supposedly fixed in 5.1.0-rc1 but it's still an issue
          // Otherwise, only the <Markdown> styles would do
          <View style={{textAlign: 'center'}}>
            <Markdown>{adhocPromotionMessage}</Markdown>
          </View>
        ) : (
          <>
            <Text style={{fontWeight: 'bold'}}>{title}</Text>
            {body.map((line, index) => (
              <Text key={`${line}${index}`}>{line}</Text>
            ))}
          </>
        )}
        <Text>{`ENTRIES CLOSE ${formatDate(endDate)}`}</Text>
      </View>
      {!!promotion.game && (
        <GamePlayActions
          activation={promotion}
          navigation={navigation}
          gameParams={summerCashGrabConfig}
          onNotEnterGame={onNotEnterGame}
        />
      )}
    </>
  );
};

const DrawnWinnersView = ({promotion}: {promotion: AirtablePromotionItem}) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();

  const heroImage = getHeroImageDetail(promotion, isWebDesktopScreen);

  return (
    <ContentView testID="promotion-detail-screen">
      <ScrollView showsVerticalScrollIndicator={false} showsHorizontalScrollIndicator={false}>
        {getFeedDiscoverDetailGraphics(heroImage, null, undefined, null)}
        <View style={styles.textCard}>
          <Text style={styles.headline}>{promotion?.headline}</Text>
          <Text style={styles.copy}>{promotion?.description}</Text>
        </View>
        <View style={styles.textCard}>
          <Text style={styles.headline}>WINNERS</Text>
          <Text style={styles.copy}>{promotion?.winners}</Text>
        </View>
      </ScrollView>
    </ContentView>
  );
};

const LoadingView = () => {
  return (
    <ContentView>
      <LoadingImg />
    </ContentView>
  );
};

export type PromotionDetailScreenProps = RouteNavigatorScreenProps<AppRoute.PROMOTION> & {
  onOnlineOrderAfterSubmit?: () => void;
  onOnlineOrderCancel?: () => void;
  isLastCompetitionRemaining: boolean; // Summer Cash Grab
};

type PromotionDetailScreenStandaloneProps = {
  promotion?: AirtablePromotionItem;
  orderNumber?: string;
  onOnlineOrderAfterSubmit?: () => void;
  onOnlineOrderCancel?: () => void;
  isLastCompetitionRemaining?: boolean; // Summer Cash Grab
  navigation: PromotionDetailScreenProps['navigation'];
  isRootStoreSelectionFlow?: boolean;
  extraContent?: React.ReactNode;
};
export const PromotionDetailScreenStandalone = observer(
  ({
    promotion,
    orderNumber,
    onOnlineOrderAfterSubmit = () => {},
    onOnlineOrderCancel = () => {},
    isLastCompetitionRemaining = false,
    navigation,
    isRootStoreSelectionFlow = false,
    extraContent,
  }: PromotionDetailScreenStandaloneProps) => {
    const isOnlineOrder = !!orderNumber;

    const {cartStore, shopSelectionStore, userStore} = useStores();
    const isLoggedIn = userStore.isLoggedIn;
    const isSelectedStoreLoyaltyEnabled = shopSelectionStore.loyaltyEnabled;

    const [isLoading, setLoading] = useState(false);
    const [linkedProducts, setLinkedProducts] = useState<OrderLine[]>([]);
    // Start in the loading state whenever a linked-products fetch is imminent
    // (store selected + promotion has barcodes) so the "not available" empty
    // notice can't flash on first paint, before the load effect runs. Lazy so
    // the no-store case stays false and doesn't hang on a permanent spinner.
    const [isLoadingLinkedProducts, setIsLoadingLinkedProducts] = useState(
      () => !!(promotion && shopSelectionStore.selectedStore && promotion.barcodes?.length),
    );

    const [isSignInModalVisible, setIsSignInModalVisible] = useState(false);
    const [isCapturingDetails, setIsCapturingDetails] = useState(false);
    const [isSubmittingEntry, setIsSubmittingEntry] = useState(false);
    const [isSubmittedEntry, setIsSubmittedEntry] = useState(false);
    const [submissionResponse, setSubmissionResponse] = useState<CreateCompetitionEntryResponse>();

    const [shouldDisplayDescription, setShouldDisplayDescription] = useState(
      isOnlineOrder ? false : !isCapturingDetails,
    );

    const [isReadyToCaptureDetails, setIsReadyToCaptureDetails] = useState(
      // isOnlineOrder && !promotion?.game ? true : isCapturingDetails,
      isOnlineOrder ? true : isCapturingDetails,
    );

    const resetFormState = useCallback(() => {
      setIsCapturingDetails(false);
      setIsSubmittedEntry(false);
      setIsSubmittingEntry(false);
      setSubmissionResponse(undefined);
      setIsSignInModalVisible(false);
      setShouldDisplayDescription(isOnlineOrder ? false : true);
      setIsReadyToCaptureDetails(isOnlineOrder ? true : false);
    }, [isOnlineOrder]);

    // Reset form state when a different promotion is navigated to (Tab navigator reuses the same screen instance)
    useEffect(() => {
      resetFormState();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promotion?.id]);

    // Reset form state on screen re-focus (handles same-promotion re-entry on mobile Tab navigator)
    useFocusEffect(
      useCallback(() => {
        resetFormState();
      }, [resetFormState]),
    );

    const {isWebDesktopScreen} = useWebScreenDimensions();
    // DIS-1127: on web mobile the AnimatedTabBar is absolute-positioned and
    // overlays the bottom ~52 px of this ScrollView. Without paddingBottom,
    // the last linked-product tile's Add-to-Cart button hides under it.
    // On native (per DIS-1092 / PR #216) and on web desktop this returns 0.
    const tabBarPadding = useTabBarBottomPadding();

    //loading linked products for the promotions
    useEffect(() => {
      if (promotion) {
        if (shopSelectionStore.selectedStore) {
          (async function () {
            setLinkedProducts([]);
            setIsLoadingLinkedProducts(true);
            const linked = await loadLinkedProducts(promotion);
            setLinkedProducts(linked as OrderLine[]);
            setIsLoadingLinkedProducts(false);
          })();
        }
      }
    }, [promotion, shopSelectionStore.selectedStore]);

    useEffect(() => {
      if (promotion) {
        Analytics.logEvent({
          eventName: EventName.ViewActivation,
          parameters: {
            promotion_name: promotion.activationUrlName,
            competition_id: promotion.id,
            order_number: orderNumber || '',
          },
        });
      }
      // only once
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!promotion) {
      return (
        <View testID="promotion-detail-screen-promotion-not-found">
          <Text>Promotion not found</Text>
        </View>
      );
    }

    const showMemberOnlyPromo = canViewMembersOnlyPromotion(promotion, {
      isLoggedIn,
      loyaltyEnabled: isSelectedStoreLoyaltyEnabled,
    });
    if (!showMemberOnlyPromo) {
      return (
        <MemberOnlyAccessView
          isLoggedIn={isLoggedIn}
          onSignIn={() => navigation.navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN)}
        />
      );
    }

    const showErrorToast = (message: string) =>
      Toast.show({
        type: ERROR_MESSAGE,
        text1: message,
      });

    async function handleSubmit(data: PromotionUserSubmission) {
      if (!promotion) {
        logger.error('PromotionDetailScreen', 'Error submitting competition entry: No promotion');
        showErrorToast('There was an issue receiving your entry, please try again');
        return;
      }

      setIsSubmittingEntry(true);

      try {
        const response = await apiService.createCompetitionEntry({
          ...data,
          // ⚠️ Beware: We had an issue where the backend was receiving the same value for
          // `orderNumber` and `orderId`. Before, we were using `orderId` as `route.params.orderId`.
          // The change done that seems to get it fixed is:
          // https://dev.azure.com/lmgdevops/branded_website/_git/branded_website/commit/072681e058a407554c207a47c647c9dfc67a7033?refName=refs/heads/main&path=/frontend/app/components/AddressPicker.js
          // Which is just the line below 🤷🏽‍♂️
          // Also, only send orderId if we have orderNumber
          orderId: orderNumber && cartStore.currentOrder ? cartStore.currentOrder.id : undefined,
          orderNumber,
          competitionId: promotion.id,
          promotionName: promotion.activationUrlName,
          selectedStoreName: shopSelectionStore?.selectedStore?.name || '',
          thirdPartyId: promotion.mflId,
        });
        setSubmissionResponse(response);
        setIsSubmittedEntry(true);
        if (!promotion.game) {
          onOnlineOrderAfterSubmit();
        }
      } catch (err: unknown) {
        // TODO: We need a global way of detecting 401s from MFL and notifying the user
        // https://dev.azure.com/lmgdevops/branded_website/_workitems/edit/1750
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        // DIS-1563 made the entry gate enforce store targeting on drawn competitions, so a
        // 403 "Store not eligible" is now reachable here. It is an expected business
        // rejection, not a fault: retrying cannot succeed on this store, so say why rather
        // than inviting the user to try again forever (DIS-1564).
        if ((err as AxiosError)?.response?.status === 403) {
          logger.info(
            'PromotionDetailScreen',
            'Competition entry rejected: store not eligible',
            serializeError(err),
          );
          showErrorToast('This competition is not available at your selected store');
        } else if (errorMessage !== 'Unauthorized') {
          logger.error(
            'PromotionDetailScreen',
            'Error submitting competition entry',
            serializeError(err),
          );
          showErrorToast('There was an issue receiving your entry, please try again');
        }
        onOnlineOrderCancel();
      }
      setIsSubmittingEntry(false);
    }

    async function handleSubmitForGame() {
      setIsSubmittingEntry(true);

      try {
        const response = await apiService.createGameCompetitionEntry();
        setSubmissionResponse(response);
        setIsSubmittedEntry(true);
        navigation.navigate(AppRoute.SUMMER_CASH_GRAB_GAME, {
          url: promotion?.iFrameLink,
          iFrameLinkParams: response.meta?.game?.SUMMER_CASH_GRAB?.iFrameLinkParams,
        });
      } catch (err: unknown) {
        // TODO: We need a global way of detecting 401s from MFL and notifying the user
        // https://dev.azure.com/lmgdevops/branded_website/_workitems/edit/1750
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        if (errorMessage !== 'Unauthorized') {
          logger.error(
            'PromotionDetailScreen',
            'Error submitting game competition entry',
            serializeError(err),
          );
          showErrorToast('There was an issue receiving your entry, please try again');
        }
        onOnlineOrderCancel();
      }
      setIsSubmittingEntry(false);
    }

    // This is only visible for entries that are part of the post
    // order flow. Need to give the user some way to skip entering.
    const handleOnlineOrderCancel = onOnlineOrderCancel;

    const tryGetWebWindowReference = () => {
      // Hotfix: Safari hack
      // When `promotionItem.offersStore`, Safari will block the "popup" window
      // given the async nature of the `transferSession` call and the fact that in
      // our current version of `react-native-web` we can't specify a target for
      // `Linking.openURL`. react-native-web now supports "Allow explicit target
      // for Linking.openURL(), default to "_blank" but when upgrading to 0.19.x
      // I got stuff like:
      // "Can't resolve 'react-native-web/dist/exports/PermissionsAndroid'..."
      // (which is effectively removed: https://github.com/necolas/react-native-web/compare/0.18.12...0.19.0)
      // References:
      // - 2093-[TechDebt]Upgrade react-native-web to 0.19.x: https://dev.azure.com/lmgdevops/branded_website/_workitems/edit/2093)
      // - Allow explicit target for Linking.openURL(), default to "_blank" #2277: https://github.com/necolas/react-native-web/pull/2277
      if (Platform.OS === 'web') {
        try {
          return window.open('about:blank', '_blank');
        } catch (error) {
          logger.error('PromotionDetailScreen', 'Error opening external microsite', {error});
        }
      }
    };

    const handleExternalMicrosite = async () => {
      let params = '';
      let webWindowReference;

      if (promotion.offersStore) {
        webWindowReference = tryGetWebWindowReference();
        setLoading(true);
        const transferTokens = await apiService.transferSession();
        if (transferTokens) {
          params = `${transferTokens.parameter}=${transferTokens.token}`;
        }
        setLoading(false);
      }

      let url = promotion.externalMicrosite.trim();
      if (!url.startsWith('https://')) url = `https://${url}`;
      if (params) {
        if (url.includes('?')) url = `${url}&${params}`;
        else url = `${url}?${params}`;
      }

      if (webWindowReference) {
        // Safari hack
        webWindowReference.location = url;
      } else {
        await Linking.openURL(url);
      }
    };

    const handleIframeLink = () => {
      if (promotion.game) {
        navigation.navigate(AppRoute.SUMMER_CASH_GRAB_GAME, {
          url: promotion.iFrameLink,
        });
        return;
      }

      navigation.navigate(AppRoute.PROMOTION_IFRAME, {
        url: promotion.iFrameLink,
      });
    };

    const handleShoppedInStorePress = async () => {
      if (promotion.externalMicrosite) {
        await handleExternalMicrosite();
        return;
      }

      if (promotion.iFrameLink) {
        handleIframeLink();
        return;
      }

      if (!isLoggedIn) {
        setIsSignInModalVisible(true);
      } else {
        setIsCapturingDetails(true);
        setShouldDisplayDescription(false);
        setIsReadyToCaptureDetails(true);
      }
    };

    const handleNotEnterGame = isOnlineOrder ? handleOnlineOrderCancel : navigation.goBack;

    const heroImage = getHeroImageDetail(promotion, isWebDesktopScreen);
    const termsAndConditionsUrl = getTermsAndConditionsUrl(promotion);

    const isEcommerceOnly = isEcommerceOnlyPromotion(promotion);
    const isInStoreOnly = isInStoreOnlyPromotion(promotion);
    const isEnterHereButtonVisible =
      !promotion.noEntryPromo && !isEcommerceOnly && isInStoreOnly && showMemberOnlyPromo;
    const isReceiptRequired = !promotion.noReceiptRequired;

    const isDrawnWinners = isDrawnWinnersPromotion(promotion);
    if (isDrawnWinners) {
      return <DrawnWinnersView promotion={promotion} />;
    }

    if (isLoading || !promotion || isSubmittingEntry) {
      return <LoadingView />;
    }

    return (
      <ContentView testID="promotion-detail-screen">
        {/* KeyboardAwareScrollView (not plain ScrollView) so keyboard-covered
            content clears the on-screen keyboard. DIS-1513: the gift-card
            "CHECK STORE LOCATION" suburb list renders as inline content below
            its postcode input; a plain ScrollView leaves it trapped under the
            keyboard (iOS: no content inset; Android: manifest adjustPan pans
            but never resizes). enableOnAndroid applies the keyboard inset on
            Android too. */}
        <KeyboardAwareScrollView
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          enableOnAndroid
          extraScrollHeight={40}
          contentContainerStyle={{paddingBottom: tabBarPadding}}>
          {getFeedDiscoverDetailGraphics(heroImage, null, undefined, null)}
          <View style={styles.textCard}>
            <Text style={styles.headline}>{promotion?.headline}</Text>
            {!!shouldDisplayDescription && <Markdown>{promotion?.description}</Markdown>}
          </View>
          {isSubmittedEntry ? (
            <SuccessView
              activation={promotion}
              navigation={navigation}
              createCompetitionEntryResponse={submissionResponse}
              onNotEnterGame={handleNotEnterGame}
            />
          ) : isReadyToCaptureDetails ? (
            <PromotionCaptureDetails
              activation={promotion}
              onSubmit={promotion.game ? handleSubmitForGame : handleSubmit}
              orderNumber={orderNumber}
              onCancel={handleOnlineOrderCancel}
              isReceiptRequired={isReceiptRequired}
              isLastCompetitionRemaining={isLastCompetitionRemaining}
            />
          ) : (
            <>
              {/* {!!promotion.game && (
              <GamePlayActions
                activation={promotion}
                navigation={navigation}
                isPracticePlay
                onNotEnterGame={navigation.goBack}
              />
            )} */}
              {!!isEnterHereButtonVisible && (
                <Button
                  onPress={handleShoppedInStorePress}
                  title={getEnterHereButtonText(promotion)}
                  buttonStyle={styles.enterHereButton}
                />
              )}
              {!!termsAndConditionsUrl && (
                <View style={styles.termsAndConditions}>
                  <OpenURLButton
                    url={termsAndConditionsUrl}
                    textStyle={styles.termsAndConditionsLink}>
                    Terms and conditions
                  </OpenURLButton>
                </View>
              )}
              {isGiftCardsPromotion(promotion) && (
                <GiftCardPromotionExtraContent
                  navigation={navigation}
                  isRootStoreSelectionFlow={isRootStoreSelectionFlow}
                />
              )}
              {!!extraContent && extraContent}
              {!shopSelectionStore.ecommerceDisabled &&
                promotion?.barcodes?.length > 0 &&
                (isLoadingLinkedProducts ? (
                  <ContentView>
                    <ActivityIndicator />
                  </ContentView>
                ) : (
                  <View style={styles.productList}>
                    <PromotionProductListing key={promotion?.id} items={linkedProducts} />
                  </View>
                ))}
            </>
          )}
          <PromotionUserSignInModal
            isVisible={isSignInModalVisible}
            onNavigate={() => {
              setIsSignInModalVisible(false);
              navigation.navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN);
            }}
          />
        </KeyboardAwareScrollView>
      </ContentView>
    );
  },
);

const PromotionDetailScreen = observer(
  ({
    route,
    navigation,
    onOnlineOrderAfterSubmit = () => {},
    onOnlineOrderCancel = () => {},
    isLastCompetitionRemaining = true,
  }: PromotionDetailScreenProps) => {
    const promotion = route.params?.promotion;
    const orderNumber = route.params?.orderNumber;

    // Handle deep linking "404". TODO: Better way? In "linking.ts"?
    useEffect(() => {
      if (!route.params?.promotion) navigation.navigate(AppRoute.HOME);
    }, [navigation, route.params]);

    return (
      <PromotionDetailScreenStandalone
        promotion={promotion}
        orderNumber={orderNumber}
        onOnlineOrderAfterSubmit={onOnlineOrderAfterSubmit}
        onOnlineOrderCancel={onOnlineOrderCancel}
        isLastCompetitionRemaining={isLastCompetitionRemaining}
        navigation={navigation}
      />
    );
  },
);

export default PromotionDetailScreen;

const styles = StyleSheet.create({
  textCard: {
    marginTop: 24,
  },
  termsAndConditions: {
    marginTop: 16,
    alignItems: 'center',
  },
  termsAndConditionsLink: {
    fontSize: 14,
    color: theme.colour.text.link.primary,
  },
  enterText: {
    fontSize: 16,
    color: 'white',
  },
  headline: {
    fontSize: 20,
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  productList: {
    marginTop: 32,
    marginBottom: 4,
  },
  enterHereButton: {
    marginTop: 16,
    width: '80%',
  },
  copy: {
    fontSize: 16,
  },
  linkedProductsActivityIndicator: {
    flex: 1,
  },
});
